import axios from 'axios'
import Cookies from 'js-cookie'

const state = {
  gymEquipments: [],
  gymInfo: null,
  activeTrainings: [],
  draggedFighter: null,
  showTrainingModal: false,
  trainingTimers: {} // Add this to store training countdown timers
}

const mutations = {
  SET_GYM_INFO(state, info) {
    state.gymInfo = info
  },
  SET_GYM_EQUIPMENTS(state, equipments) {
    state.gymEquipments = equipments
  },
  SET_ACTIVE_TRAININGS(state, trainings) {
    state.activeTrainings = trainings || []
    
    // Initialize timers for each training
    if (trainings && trainings.length) {
      trainings.forEach(training => {
        if (training.secondsRemaining > 0) {
          state.trainingTimers[training.id] = training.secondsRemaining
        }
      })
    }
  },
  SET_DRAGGED_FIGHTER(state, fighter) {
    state.draggedFighter = fighter
  },
  SET_SHOW_TRAINING_MODAL(state, show) {
    state.showTrainingModal = show
  },
  UPDATE_TRAINING_TIMER(state, { trainingId, seconds }) {
    state.trainingTimers = {
      ...state.trainingTimers,
      [trainingId]: seconds
    }
  },
  REMOVE_TRAINING_TIMER(state, trainingId) {
    const newTimers = { ...state.trainingTimers }
    delete newTimers[trainingId]
    state.trainingTimers = newTimers
  }
}

const actions = {
  // Get gym data from player state instead of API call
  fetchGymData({ commit, rootState }) {
    try {
      const playerData = rootState.player.playerData
      
      if (playerData) {
        // Set gym info from player data
        if (playerData.gym) {
          commit('SET_GYM_INFO', playerData.gym)
        }
        
        // Active trainings derived from each fighter's training (fighters.training is populated)
        const trainings = (playerData.fighters || [])
          .filter(fighter => fighter.training && fighter.training.endDate)
          .map(fighter => ({
            ...fighter.training,
            fighter
          }))
        commit('SET_ACTIVE_TRAININGS', trainings)
        
        // Get gym equipments (those that have a gym property)
        if (playerData.inventory && playerData.inventory.gym_equipments) {
          const gymEquipments = playerData.inventory.gym_equipments.filter(eq => eq.gym)
          commit('SET_GYM_EQUIPMENTS', gymEquipments)
        }
      }
      
      return playerData
    } catch (error) {
      console.error('Failed to fetch gym data:', error)
      throw error
    }
  },

  async startTraining({ dispatch }, { gymId, characterId, equipmentId }) {
    try {
      // Updated to use the correct training API endpoint
      await axios.post(
        `${process.env.VUE_APP_API_URL}/api/trainings/start`,
        { 
          gymId, 
          characterId, 
          equipmentId,
          characterType: 'fighter'  // Specify that we're training a fighter
        },
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      )
      
      // Refresh player data after starting training
      await dispatch('player/loadPlayerData', null, { root: true })
      // Update local state with fresh player data
      dispatch('fetchGymData')
    } catch (error) {
      console.error('Failed to start training:', error)
      throw error
    }
  },
  
  async startRest({ dispatch }, { gymId, characterId }) {
    return dispatch('startStaminaRecovery', { gymId, fighterId: characterId })
  },

  async equipGymEquipment({ dispatch }, { gymId, equipmentId }) {
    const token = Cookies.get('jwt')
    try {
      await axios.post(
        `${process.env.VUE_APP_API_URL}/api/gyms/${gymId}/equip`,
        { equipmentId },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      // Refresh player data, which contains all gym info
      await dispatch('player/loadPlayerData', null, { root: true })
      // Update local state with fresh player data
      dispatch('fetchGymData')
    } catch (error) {
      console.error('Error equipping gym equipment:', error)
      throw error
    }
  },
  
  async unequipGymEquipment({ dispatch }, { gymId, equipmentId }) {
    const token = Cookies.get('jwt')
    try {
      await axios.post(
        `${process.env.VUE_APP_API_URL}/api/gyms/${gymId}/unequip`,
        { equipmentId },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      // Refresh player data, which contains all gym info
      await dispatch('player/loadPlayerData', null, { root: true })
      // Update local state with fresh player data
      dispatch('fetchGymData')
    } catch (error) {
      console.error('Error unequipping gym equipment:', error)
      throw error
    }
  },
  
  async startStaminaRecovery({ dispatch }, { gymId, fighterId }) {
    try {
      // Rest on a mattress (Colchões) installed in the gym
      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}/api/resting/create`,
        {
          gymId,
          fighterId
        },
        {
          headers: {
            Authorization: `Bearer ${Cookies.get('jwt')}`
          }
        }
      );

      if (response.data) {
        // Refresh player data, which contains all gym info
        await dispatch('player/loadPlayerData', null, { root: true })
        // Update local state with fresh player data
        dispatch('fetchGymData')
        return response.data;
      }
    } catch (error) {
      console.error('Error starting stamina recovery:', error);
      throw new Error(error.response?.data?.error?.message || error.message);
    }
  },
  
  // Add a new action to decrement timers
  decrementTrainingTimer({ commit, state, dispatch }, trainingId) {
    if (state.trainingTimers[trainingId] > 0) {
      commit('UPDATE_TRAINING_TIMER', { 
        trainingId, 
        seconds: state.trainingTimers[trainingId] - 1 
      })
      
      // If timer reaches zero, remove it and refresh data
      if (state.trainingTimers[trainingId] <= 0) {
        commit('REMOVE_TRAINING_TIMER', trainingId)
        dispatch('player/loadPlayerData', null, { root: true })
        dispatch('fetchGymData')
      }
    }
  },
  
  // Add action to get current timer value
  getTrainingTimer({ state }, trainingId) {
    return state.trainingTimers[trainingId] || 0
  },

  setDraggedFighter({ commit }, fighter) {
    commit('SET_DRAGGED_FIGHTER', fighter)
  },
  
  setShowTrainingModal({ commit }, show) {
    commit('SET_SHOW_TRAINING_MODAL', show)
  }
}

const getters = {
  getGymEquipments: state => state.gymEquipments,
  getGymInfo: state => state.gymInfo,
  getActiveTrainings: state => state.activeTrainings,
  getDraggedFighter: state => state.draggedFighter,
  getShowTrainingModal: state => state.showTrainingModal,
  getTrainingTimers: state => state.trainingTimers
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
